import {
  isTrainerCoordinatedAtDate,
  isTrainerCoordinationEnabled,
  parseTrainerCoordinationPeriods,
  resolveTrainerCoordinatorForDate,
} from "./trainer-coordination.js";
import {
  isSessionBillingTrainerSelfManaged,
  isSessionTrainerSelfPlannedForBilling,
} from "./session-billing-ownership.js";

const ISSUER_CHOICE_SEP = "::";
const ISSUER_MODE_COORDINATOR = "coordinator";
const ISSUER_MODE_TRAINER = "trainer";

/** Platzhalter für gemeinsame Koordinator-Rechnung ohne festen Trainer-Bezug. */
export const BILLING_ISSUER_COORDINATOR_PLACEHOLDER = "__coordinator__";

function nz(v) {
  return String(v ?? "").trim();
}

/** Nur echte Trainer-IDs für Koordinator-Lookup — keine Issuer-Wahl-Codes. */
function normalizeBillingCoordinatorLookupTrainerId(raw) {
  const s = nz(raw);
  if (!s || s.includes(ISSUER_CHOICE_SEP)) return "";
  if (isBillingIssuerCoordinatorPlaceholder(s)) return "";
  return s;
}

/** @param {string} trainerId */
export function isBillingIssuerCoordinatorPlaceholder(trainerId) {
  return nz(trainerId) === BILLING_ISSUER_COORDINATOR_PLACEHOLDER;
}

/**
 * Trainer-ID, die in Koordinations-Zeiträumen am häufigsten referenziert wird.
 *
 * @param {object[]} trainers
 * @returns {string}
 */
export function findCoordinatorTrainerIdFromPeriodReferences(trainers) {
  const list = Array.isArray(trainers) ? trainers : [];
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const tr of list) {
    for (const period of parseTrainerCoordinationPeriods(tr)) {
      const cid = nz(period?.coordinator_trainer_id);
      if (!cid) continue;
      counts.set(cid, (counts.get(cid) || 0) + 1);
    }
  }
  let bestId = "";
  let bestCount = 0;
  for (const [id, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * Trainingskoordinator für Rechnungssteller-Auswahl und -Anzeige.
 *
 * @param {{
 *   trainers?: object[],
 *   coordinatorTrainerId?: string,
 *   preferredTrainerId?: string,
 *   viewerTrainerId?: string,
 *   allowViewerTrainerFallback?: boolean,
 * }} input
 * @returns {string}
 */
export function resolveBillingCoordinatorTrainerId(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const list = (Array.isArray(safe.trainers) ? safe.trainers : []).filter(
    (tr) => tr?.is_inactive !== true,
  );

  const explicitId =
    normalizeBillingCoordinatorLookupTrainerId(safe.coordinatorTrainerId) ||
    normalizeBillingCoordinatorLookupTrainerId(safe.preferredTrainerId);
  if (explicitId) return explicitId;

  const plannerId = resolvePlannerTrainerIdFromList(list);
  if (plannerId) return plannerId;

  const fromPeriodRefs = findCoordinatorTrainerIdFromPeriodReferences(list);
  if (fromPeriodRefs) return fromPeriodRefs;

  if (safe.allowViewerTrainerFallback === false) return "";
  return nz(safe.viewerTrainerId);
}

/**
 * @param {{
 *   coordinatorTrainerId?: string,
 *   coordinatedCoordinatorId?: string,
 *   trainers?: object[],
 *   items?: object[],
 *   sessions?: object[],
 *   viewerTrainerId?: string,
 * }} input
 */
function resolveCoordinatorTrainerIdForIssuerDisplay(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const trainerList = Array.isArray(safe.trainers) ? safe.trainers : [];
  const fromExplicit = resolveBillingCoordinatorTrainerId({
    trainers: trainerList,
    coordinatorTrainerId: safe.coordinatorTrainerId,
    preferredTrainerId: safe.preferredTrainerId,
    viewerTrainerId: safe.viewerTrainerId,
    allowViewerTrainerFallback: true,
  });
  if (fromExplicit) return fromExplicit;

  const coordinatedCoordinatorId = nz(safe.coordinatedCoordinatorId);
  if (coordinatedCoordinatorId) return coordinatedCoordinatorId;

  return resolveBillingCoordinatorIdForCoordinatedStatement({
    items: safe.items,
    sessions: safe.sessions,
    trainers: trainerList,
    coordinatorTrainerId: nz(safe.coordinatorTrainerId),
  });
}

/**
 * @param {string} raw
 * @returns {{ trainerId: string, mode: 'coordinator'|'trainer'|null }}
 */
export function decodeBillingStatementIssuerChoice(raw) {
  const s = nz(raw);
  if (!s) return { trainerId: "", mode: null };
  const sep = s.lastIndexOf(ISSUER_CHOICE_SEP);
  if (sep > 0) {
    const mode = s.slice(sep + ISSUER_CHOICE_SEP.length).toLowerCase();
    if (mode === ISSUER_MODE_COORDINATOR || mode === ISSUER_MODE_TRAINER) {
      return { trainerId: s.slice(0, sep), mode };
    }
  }
  return { trainerId: s, mode: null };
}

/**
 * Persistierter Draft-Aussteller vorhanden (bare ID oder kodierte Wahl)?
 * @param {string|null|undefined} draftIssuerTrainerId
 */
export function hasPersistedDraftIssuer(draftIssuerTrainerId) {
  return nz(draftIssuerTrainerId) !== "";
}

/**
 * Trainer-ID aus persistiertem Draft-Aussteller (ohne Live-Auflösung).
 * @param {string|null|undefined} draftIssuerTrainerId
 */
export function resolvePersistedDraftIssuerTrainerId(draftIssuerTrainerId) {
  return decodeBillingStatementIssuerChoice(draftIssuerTrainerId).trainerId;
}

/**
 * Präferenz für Preview/Versand aus `draft_issuer_trainer_id`.
 *
 * C1: Jeder nicht-leere Wert (bare Trainer-ID oder …::trainer / …::coordinator)
 * ist für Entwürfe verbindlich und darf nicht still ignoriert werden.
 *
 * @param {string} draftIssuerTrainerId
 * @param {string} [status]
 */
export function resolveDraftIssuerPreferenceForPreview(
  draftIssuerTrainerId,
  status = "draft",
) {
  const raw = nz(draftIssuerTrainerId);
  if (!raw) return "";
  return raw;
}

/**
 * Rechnungssteller-Präferenz: Draft-Persistenz zuerst, sonst Snapshot (versendet).
 *
 * @param {object | null | undefined} statement
 * @param {string} [fallbackPreferred]
 */
export function resolveBillingStatementIssuerPreferenceForPreview(
  statement,
  fallbackPreferred = "",
) {
  const st = statement && typeof statement === "object" ? statement : {};
  const status = nz(st.status) || "draft";
  const draftPref = resolveDraftIssuerPreferenceForPreview(
    st.draft_issuer_trainer_id,
    status,
  );
  if (status.toLowerCase() === "draft") return draftPref;
  return (
    draftPref || nz(st.issuer_trainer_id_snapshot) || nz(fallbackPreferred)
  );
}

/**
 * @param {object[]} trainers
 */
export function resolvePlannerTrainerIdFromList(trainers) {
  const list = Array.isArray(trainers) ? trainers : [];
  const planner = list.find(
    (tr) => nz(tr?.login_role).toLowerCase() === "planner",
  );
  return planner?.id ? nz(planner.id) : "";
}

/**
 * @param {object[]} items
 * @param {object[]} sessions
 * @returns {Array<{ trainerId: string, date: string }>}
 */
export function collectBillingStatementItemTrainerDates(items, sessions) {
  const sessionById = new Map(
    (Array.isArray(sessions) ? sessions : []).map((s) => [nz(s?.id), s]),
  );
  const out = [];
  for (const it of Array.isArray(items) ? items : []) {
    const sid = nz(it?.session_id);
    const session = sid ? sessionById.get(sid) : null;
    const trainerId = nz(session?.trainer_id) || nz(it?.trainer_id);
    if (!trainerId) continue;
    const date = nz(it?.date).slice(0, 10) || nz(session?.date).slice(0, 10);
    out.push({ trainerId, date, sessionId: sid || "" });
  }
  return out;
}

/**
 * Legacy-Fallback ohne coordination_periods: Session-Eigenplanung vs. Koordinator-Planung.
 *
 * @param {Array<{ trainerId: string, date: string, sessionId?: string }>} entries
 * @param {object[]} sessions
 * @param {Map<string, object>} trainersById
 * @returns {boolean|undefined}
 */
function computeLegacyCoordinatorPreferenceFromSessionOwnership(
  entries,
  sessions,
  trainersById,
) {
  const sessionById = new Map(
    (Array.isArray(sessions) ? sessions : []).map((s) => [nz(s?.id), s]),
  );
  let hasCoordinatorManaged = false;
  let hasSelfManaged = false;

  for (const entry of entries) {
    const tid = nz(entry?.trainerId);
    if (!tid) continue;
    const trainer = trainersById.get(tid);
    const session = sessionById.get(nz(entry?.sessionId)) || {
      trainer_id: tid,
      date: nz(entry?.date).slice(0, 10),
    };
    if (isSessionTrainerSelfPlannedForBilling(session, trainer)) {
      hasSelfManaged = true;
    } else {
      hasCoordinatorManaged = true;
    }
  }

  if (!hasCoordinatorManaged && !hasSelfManaged) return undefined;
  if (hasCoordinatorManaged && !hasSelfManaged) return true;
  if (hasSelfManaged && !hasCoordinatorManaged) return false;
  return false;
}

/**
 * @param {{
 *   items?: object[],
 *   sessions?: object[],
 *   trainers?: object[],
 *   entries?: Array<{ trainerId: string, date: string }>,
 * }} input
 * @returns {boolean|undefined}
 */
export function computeBillingStatementCoordinationPreference(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const trainerList = Array.isArray(safe.trainers) ? safe.trainers : [];
  const trainersById = new Map(trainerList.map((tr) => [nz(tr?.id), tr]));
  const sessionById = new Map(
    (Array.isArray(safe.sessions) ? safe.sessions : []).map((s) => [
      nz(s?.id),
      s,
    ]),
  );
  const entries = Array.isArray(safe.entries)
    ? safe.entries
    : collectBillingStatementItemTrainerDates(safe.items, safe.sessions);

  const anyConfigured = trainerList.some((tr) =>
    isTrainerCoordinationEnabled(tr),
  );
  if (!anyConfigured) {
    return computeLegacyCoordinatorPreferenceFromSessionOwnership(
      entries,
      safe.sessions,
      trainersById,
    );
  }

  /** @type {Map<string, boolean>} */
  const perTrainer = new Map();
  for (const entry of entries) {
    const tid = nz(entry?.trainerId);
    if (!tid) continue;
    const date = nz(entry?.date).slice(0, 10);
    const trainer = trainersById.get(tid);
    const session = sessionById.get(nz(entry?.sessionId)) || {
      trainer_id: tid,
      date,
      created_by: entry?.createdBy,
    };
    if (isSessionBillingTrainerSelfManaged(session, trainer)) continue;
    const coordinated = date
      ? isTrainerCoordinatedAtDate(trainer, date)
      : false;
    perTrainer.set(
      tid,
      perTrainer.has(tid) ? perTrainer.get(tid) && coordinated : coordinated,
    );
  }

  if (perTrainer.size === 0) return undefined;
  return [...perTrainer.values()].every(Boolean);
}

/**
 * @param {{
 *   items?: object[],
 *   sessions?: object[],
 *   trainers?: object[],
 *   entries?: Array<{ trainerId: string, date: string }>,
 *   coordinatorTrainerId?: string,
 * }} input
 */
export function resolveBillingCoordinatorIdForCoordinatedStatement(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const trainerList = Array.isArray(safe.trainers) ? safe.trainers : [];
  const trainersById = new Map(trainerList.map((tr) => [nz(tr?.id), tr]));
  const fallback =
    nz(safe.coordinatorTrainerId) ||
    resolvePlannerTrainerIdFromList(trainerList);

  const sessionById = new Map(
    (Array.isArray(safe.sessions) ? safe.sessions : []).map((s) => [
      nz(s?.id),
      s,
    ]),
  );
  const entries = Array.isArray(safe.entries)
    ? safe.entries
    : collectBillingStatementItemTrainerDates(safe.items, safe.sessions);

  for (const entry of entries) {
    const tid = nz(entry?.trainerId);
    const date = nz(entry?.date).slice(0, 10);
    if (!tid || !date) continue;
    const trainer = trainersById.get(tid);
    const session = sessionById.get(nz(entry?.sessionId));
    if (session && isSessionBillingTrainerSelfManaged(session, trainer))
      continue;
    if (!isTrainerCoordinatedAtDate(trainer, date)) continue;
    const fromPeriod = resolveTrainerCoordinatorForDate(trainer, date);
    if (fromPeriod) return fromPeriod;
    if (fallback) return fallback;
  }

  return fallback;
}

/**
 * Trainer-Stammdaten für Rechnungssteller-Auflösung (Session-Trainer + Koordinator/Planner).
 *
 * @param {object[]} allTrainers
 * @param {Parameters<typeof collectBillingStatementIssuerTrainerLookupIds>[0]} input
 */
export function selectTrainersForBillingIssuerResolution(
  allTrainers,
  input = {},
) {
  const list = Array.isArray(allTrainers) ? allTrainers : [];
  const byId = new Map(list.map((tr) => [nz(tr?.id), tr]));
  const lookupIds = collectBillingStatementIssuerTrainerLookupIds({
    ...input,
    trainers: list,
  });
  const selected = lookupIds.map((id) => byId.get(id)).filter(Boolean);
  return selected.length ? selected : list;
}

export function collectBillingStatementIssuerTrainerLookupIds(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const items = Array.isArray(safe.items) ? safe.items : [];
  const sessions = Array.isArray(safe.sessions) ? safe.sessions : [];
  const trainerList = Array.isArray(safe.trainers) ? safe.trainers : [];
  const trainersById = new Map(trainerList.map((tr) => [nz(tr?.id), tr]));
  const ids = new Set(
    collectBillingStatementItemTrainerDates(items, sessions).map(
      (e) => e.trainerId,
    ),
  );

  for (const tid of [...ids]) {
    const trainer = trainersById.get(tid);
    for (const period of parseTrainerCoordinationPeriods(trainer)) {
      const cid = nz(period?.coordinator_trainer_id);
      if (cid) ids.add(cid);
    }
  }

  const plannerId = resolvePlannerTrainerIdFromList(trainerList);
  if (plannerId) ids.add(plannerId);

  const coordinatorTrainerId = nz(safe.coordinatorTrainerId);
  if (coordinatorTrainerId) ids.add(coordinatorTrainerId);

  return [...ids].filter(Boolean);
}

function resolveDominantSessionTrainerId(items, sessions) {
  const sessionById = new Map(
    (Array.isArray(sessions) ? sessions : []).map((s) => [nz(s?.id), s]),
  );
  const counts = new Map();
  for (const it of Array.isArray(items) ? items : []) {
    const sid = nz(it?.session_id);
    if (!sid) continue;
    const tid = nz(sessionById.get(sid)?.trainer_id);
    if (!tid) continue;
    counts.set(tid, (counts.get(tid) || 0) + 1);
  }
  let bestId = "";
  let bestCount = 0;
  for (const [tid, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestId = tid;
    }
  }
  return bestId;
}

function isKnownTrainerId(trainerId, trainers, sessionTrainerIds) {
  const tid = nz(trainerId);
  if (!tid) return false;
  if (sessionTrainerIds.has(tid)) return true;
  return (Array.isArray(trainers) ? trainers : []).some(
    (tr) => nz(tr?.id) === tid,
  );
}

/**
 * Rechnungssteller für Statement-Positionen inkl. Koordinations-Zeiträume.
 *
 * @param {{
 *   items?: object[],
 *   sessions?: object[],
 *   trainers?: object[],
 *   preferredTrainerId?: string,
 *   coordinatorTrainerId?: string,
 *   billingCoordinatorView?: boolean,
 *   viewerTrainerId?: string,
 * }} input
 * @returns {string}
 */
export function resolveBillingStatementIssuerTrainerId(input = {}) {
  const safe = input && typeof input === "object" ? input : {};
  const items = Array.isArray(safe.items) ? safe.items : [];
  const sessions = Array.isArray(safe.sessions) ? safe.sessions : [];
  const trainerList = Array.isArray(safe.trainers) ? safe.trainers : [];
  const prefRaw = nz(safe.preferredTrainerId);
  const decoded = decodeBillingStatementIssuerChoice(prefRaw);
  const coordinatorTrainerId =
    nz(safe.coordinatorTrainerId) ||
    resolveBillingCoordinatorTrainerId({
      trainers: trainerList,
      coordinatorTrainerId: safe.coordinatorTrainerId,
      viewerTrainerId: safe.viewerTrainerId,
    }) ||
    resolvePlannerTrainerIdFromList(trainerList);
  const billingCoordinatorView = safe.billingCoordinatorView === true;

  const sessionTrainerIds = new Set(
    collectBillingStatementItemTrainerDates(items, sessions).map(
      (e) => e.trainerId,
    ),
  );
  const dominantSessionTrainerId = resolveDominantSessionTrainerId(
    items,
    sessions,
  );
  const preferCoordinator = computeBillingStatementCoordinationPreference({
    items,
    sessions,
    trainers: trainerList,
  });
  const coordinatedCoordinatorId =
    resolveBillingCoordinatorIdForCoordinatedStatement({
      items,
      sessions,
      trainers: trainerList,
      coordinatorTrainerId,
    });

  if (decoded.mode === ISSUER_MODE_COORDINATOR && decoded.trainerId) {
    if (isBillingIssuerCoordinatorPlaceholder(decoded.trainerId)) {
      const resolved = resolveCoordinatorTrainerIdForIssuerDisplay({
        coordinatorTrainerId,
        coordinatedCoordinatorId,
        trainers: trainerList,
        items,
        sessions,
        viewerTrainerId: nz(safe.viewerTrainerId),
        preferredTrainerId: normalizeBillingCoordinatorLookupTrainerId(
          safe.viewerTrainerId,
        ),
      });
      return resolved || coordinatorTrainerId || coordinatedCoordinatorId || "";
    }
    return decoded.trainerId;
  }

  const explicitTrainerChoice =
    prefRaw.includes(ISSUER_CHOICE_SEP) &&
    decoded.mode === ISSUER_MODE_TRAINER &&
    decoded.trainerId;
  if (explicitTrainerChoice) {
    return decoded.trainerId;
  }

  // C1: Persistierte Präferenz (bare Trainer-ID) hat Vorrang vor Live-Koordination /
  // Self-Managed-Default — kein stiller Wechsel nach Koordinationsende.
  if (prefRaw && !prefRaw.includes(ISSUER_CHOICE_SEP) && decoded.trainerId) {
    return decoded.trainerId;
  }

  const coordinatorDeskDefault =
    billingCoordinatorView &&
    coordinatorTrainerId &&
    !prefRaw.includes(ISSUER_CHOICE_SEP) &&
    preferCoordinator !== false;

  // Koordinierter Zeitraum → Default-Rechnungsadresse des Koordinators (nur unbound).
  if (
    (preferCoordinator === true || coordinatorDeskDefault) &&
    coordinatedCoordinatorId
  ) {
    return coordinatedCoordinatorId;
  }
  if (coordinatorDeskDefault && coordinatorTrainerId) {
    return coordinatorTrainerId;
  }

  if (decoded.mode === ISSUER_MODE_TRAINER && decoded.trainerId) {
    return decoded.trainerId;
  }

  if (decoded.trainerId) {
    const explicitId = decoded.trainerId;
    if (isKnownTrainerId(explicitId, trainerList, sessionTrainerIds)) {
      return explicitId;
    }
  }
  if (preferCoordinator === false && dominantSessionTrainerId) {
    return dominantSessionTrainerId;
  }

  if (dominantSessionTrainerId) return dominantSessionTrainerId;
  if (prefRaw && isKnownTrainerId(prefRaw, trainerList, sessionTrainerIds)) {
    return prefRaw;
  }
  return trainerList.length === 1 ? nz(trainerList[0]?.id) : "";
}
